import { Controller } from "@hotwired/stimulus";

const $ = window.jQuery;

// Connects to data-controller="link-report" (app/views/link_reports/preview)
//
// Two jobs, both about the row's "who posted this?" control:
//
//   1. Turn each <select> into Fomantic's search-selection dropdown. Nothing in
//      this app initialises Fomantic dropdowns globally, so a screen that wants
//      one wires it up itself.
//   2. Collect the creators the rows discover as their cards land, offer each
//      one to every row, and fill in the row that found it.
//
// (2) is what makes the control answerable. The plan is built from the URLs
// alone, so a paste of share-links — instagram.com/reel/<code>, the form every
// platform's Copy link button produces — yields no creators, and the control
// used to offer nothing but "No creator". The post itself knows who posted it;
// the row's card frame asks, and this carries the answer across to the control.
//
// Until this runs each control is a plain <select> that submits correctly, so
// it degrades rather than breaking — which matters here because rows load
// lazily and the target callbacks fire as each one arrives, in any order.
export default class extends Controller {
  static targets = ["creatorSelect", "assignmentSelect", "resolvedCreator"];

  initialize() {
    // key -> label, for every creator any row has discovered so far.
    this.discovered = new Map();
    this.rebuildQueued = false;
  }

  creatorSelectTargetConnected(select) {
    this.initDropdown(select);
    this.addMissingOptions(select);
  }

  assignmentSelectTargetConnected(select) {
    this.initDropdown(select);
  }

  resolvedCreatorTargetConnected(element) {
    const { creatorKey, creatorLabel, creatorPosition } = element.dataset;
    if (!creatorKey || !creatorLabel) return;

    const isNew = this.discovered.get(creatorKey) !== creatorLabel;
    this.discovered.set(creatorKey, creatorLabel);

    // Cards land one at a time. Rebuilding every dropdown per card is N x N
    // teardowns for no visible gain, so coalesce into one pass per frame.
    if (isNew) this.queueRebuild();

    this.preselect(creatorPosition, creatorKey);
    this.preselectAssignment(creatorPosition, creatorKey);
  }

  initDropdown(select) {
    if (!$ || select.dataset.dropdownReady) return;

    $(select).dropdown({
      fullTextSearch: true,
      // The list is the creators in this paste plus the brand's roster, so it
      // can run long. Search is the way through it; showing everything up
      // front still beats forcing a query before anything appears.
      forceSelection: false,
    });

    select.dataset.dropdownReady = "true";
  }

  queueRebuild() {
    if (this.rebuildQueued) return;
    this.rebuildQueued = true;

    requestAnimationFrame(() => {
      this.rebuildQueued = false;
      this.creatorSelectTargets.forEach((select) =>
        this.addMissingOptions(select)
      );
    });
  }

  // Fomantic builds its menu from the <select> once, at setup, and has no
  // documented way to re-read it. Adding an <option> to a live dropdown
  // therefore means tearing the dropdown down and building it again — cheap at
  // one paste's worth of rows, and it keeps the <select> the single source of
  // truth for what gets submitted.
  addMissingOptions(select) {
    const missing = [...this.discovered].filter(
      ([key]) => !this.hasOption(select, key)
    );
    if (missing.length === 0) return;

    const chosen = select.value;

    missing.forEach(([key, label]) => {
      const option = document.createElement("option");
      option.value = key;
      option.textContent = label;
      select.appendChild(option);
    });

    if ($ && select.dataset.dropdownReady) {
      $(select).dropdown("destroy");
      delete select.dataset.dropdownReady;
      this.initDropdown(select);
      if (chosen) $(select).dropdown("set selected", chosen);
    }
  }

  hasOption(select, key) {
    return [...select.options].some((option) => option.value === key);
  }

  // Never overwrite a choice. A blank value is "No creator", which on a row we
  // just identified is an absence rather than a decision; anything else is
  // either the brand's own pick or a match the plan already made, and both
  // outrank a guess from metadata.
  preselect(position, key) {
    const select = this.creatorSelectTargets.find(
      (candidate) => candidate.dataset.position === String(position)
    );
    if (!select || select.value) return;
    if (!this.hasOption(select, key)) this.addMissingOptions(select);

    select.value = key;
    if ($ && select.dataset.dropdownReady)
      $(select).dropdown("set selected", key);
  }

  // Assignment mode can only credit creators already offered by the server.
  // A late post card may select an eligible match, never add a new destination.
  preselectAssignment(position, key) {
    const select = this.assignmentSelectTargets.find(
      (candidate) => candidate.dataset.position === String(position)
    );
    if (!select || select.value) return;

    const matches = [...select.options].filter(
      (option) =>
        !option.disabled &&
        JSON.parse(option.dataset.creatorKeys || "[]").includes(key)
    );
    if (matches.length !== 1) return;

    select.value = matches[0].value;
    if ($ && select.dataset.dropdownReady)
      $(select).dropdown("set selected", select.value);
  }
}
